import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Btn from '../components/ui/Btn';
import { ApiError, api } from '../lib/api';
import { DossierListItem, formatDossierRef, PaymentMode } from '../lib/types';
import { useAuthStore } from '../stores/auth';
import { toast } from '../stores/toaster';

const ADMIN_ROLES = new Set(['admin_cabinet', 'super_admin']);

type TrashItem = {
  id: string;
  ref_cabinet: string;
  titre: string | null;
  client_name: string | null;
  deleted_at: string | null;
  purge_after: string | null;
  has_archive: boolean;
};

function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  const diff = new Date(iso).getTime() - Date.now();
  return Math.ceil(diff / 86_400_000);
}

/**
 * « Mes dossiers » — table + recherche + form de création enrichi.
 *
 * - Recherche client-side sur ref + titre + client_name (fr-locale).
 * - Form de création accepte : ref obligatoire, titre, périmètre d'accès
 *   (tous les users du tenant OU sélection de collaborateurs explicites).
 *   L'admin_cabinet voit tout par défaut, c'est géré côté backend.
 */

type TenantUser = {
  id: string;
  email: string;
  display_name: string;
  role: string;
};

function relativeAge(iso: string): string {
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const minutes = Math.round(diffMs / 60000);
  if (minutes < 60) return `il y a ${Math.max(1, minutes)} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `il y a ${hours} h`;
  const days = Math.round(hours / 24);
  if (days < 7) return `il y a ${days} j`;
  if (days < 60) return `il y a ${Math.round(days / 7)} sem`;
  const months = Math.round(days / 30);
  return `il y a ${months} mois`;
}

export default function DossiersPage() {
  const [items, setItems] = useState<DossierListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');

  const role = useAuthStore((s) => s.user?.role ?? '');
  const accessToken = useAuthStore((s) => s.accessToken);
  const isAdmin = ADMIN_ROLES.has(role);

  // Corbeille (admin only)
  const [showTrash, setShowTrash] = useState(false);
  const [trashItems, setTrashItems] = useState<TrashItem[]>([]);
  const [trashLoading, setTrashLoading] = useState(false);

  const loadTrash = async () => {
    setTrashLoading(true);
    try {
      const rows = await api.get<TrashItem[]>('/dossiers/trash');
      setTrashItems(rows);
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    } finally {
      setTrashLoading(false);
    }
  };

  const onRestore = async (d: TrashItem) => {
    try {
      await api.post(`/dossiers/${d.id}/restore`, {});
      toast.success(`Dossier ${d.ref_cabinet} restauré`);
      await Promise.all([loadTrash(), load()]);
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    }
  };

  const onPurge = async (d: TrashItem) => {
    if (
      !confirm(
        `Supprimer DÉFINITIVEMENT le dossier ${d.ref_cabinet} ?\n\n` +
          `Toutes les pièces, annotations et fichiers seront effacés de façon ` +
          `irréversible. Cette action ne peut pas être annulée.`
      )
    )
      return;
    try {
      await api.delete(`/dossiers/${d.id}/purge`);
      toast.success(`Dossier ${d.ref_cabinet} supprimé définitivement`);
      await loadTrash();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    }
  };

  const onDownloadArchive = (d: TrashItem) => {
    if (!accessToken) return;
    fetch(`/api/v1/dossiers/${d.id}/archive`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then((r) => {
        if (!r.ok) throw new Error('archive indisponible');
        return r.blob();
      })
      .then((blob) => {
        const u = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = u;
        a.download = `${d.ref_cabinet}_archive.zip`;
        a.click();
        URL.revokeObjectURL(u);
      })
      .catch(() => toast.error("Téléchargement de l'archive échoué"));
  };

  // Form de création
  const [creating, setCreating] = useState(false);
  const [refCabinet, setRefCabinet] = useState('');
  const [titre, setTitre] = useState('');
  const [clientName, setClientName] = useState('');
  const [accessMode, setAccessMode] = useState<'all' | 'restricted'>('all');
  // Sprint 2 §9.3 — null tant que l'utilisateur n'a pas tranché. Le backend
  // accepte aussi null (pas de mode) pour ne pas forcer la décision.
  const [paymentMode, setPaymentMode] = useState<PaymentMode | null>(null);
  // Date des faits — utilisée par le corpus dommage pour dériver le régime
  // (ancien CC vs Livre 6, pivot 1/1/2025). Format ISO 'YYYY-MM-DD' ou ''.
  const [dateFaits, setDateFaits] = useState('');
  // Sprint big-PDF (2026-05-22) — opt-out Mistral OCR par dossier.
  // Default true (cohérent avec le backend) : Mistral autorisé. Décocher
  // pour forcer 100% on-prem sur ce dossier (utile pour dossiers
  // ultra-sensibles : mineurs, secret instruction renforcé).
  const [mistralOcrAllowed, setMistralOcrAllowed] = useState(true);
  const [collaboratorIds, setCollaboratorIds] = useState<Set<string>>(new Set());
  const [users, setUsers] = useState<TenantUser[]>([]);
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const rows = await api.get<DossierListItem[]>('/dossiers');
      setItems(rows);
      setError(null);
    } catch (err) {
      setError(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  // Charge la liste des utilisateurs du tenant la première fois qu'on
  // ouvre le formulaire de création (utilisé par la sélection de
  // collaborateurs en mode 'restricted').
  useEffect(() => {
    if (creating && users.length === 0) {
      void api
        .get<TenantUser[]>('/auth/users')
        .then(setUsers)
        .catch(() => {
          /* silent : sélection collab indispo si l'endpoint plante */
        });
    }
  }, [creating, users.length]);

  const onCreate = async (e: FormEvent) => {
    e.preventDefault();
    if (!refCabinet.trim()) return;
    setCreateBusy(true);
    setCreateError(null);
    try {
      await api.post('/dossiers', {
        ref_cabinet: refCabinet.trim(),
        titre: titre.trim() || null,
        ...(clientName.trim() ? { client_name: clientName.trim() } : {}),
        access_mode: accessMode,
        ...(paymentMode ? { payment_mode: paymentMode } : {}),
        ...(dateFaits ? { date_faits: dateFaits } : {}),
        mistral_ocr_allowed: mistralOcrAllowed,
        collaborator_user_ids: accessMode === 'restricted' ? Array.from(collaboratorIds) : [],
      });
      setRefCabinet('');
      setTitre('');
      setClientName('');
      setAccessMode('all');
      setPaymentMode(null);
      setDateFaits('');
      setMistralOcrAllowed(true);
      setCollaboratorIds(new Set());
      setCreating(false);
      await load();
    } catch (err) {
      setCreateError(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    } finally {
      setCreateBusy(false);
    }
  };

  const toggleCollab = (id: string) => {
    setCollaboratorIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const filteredItems = useMemo(() => {
    const q = search.trim().toLocaleLowerCase('fr');
    if (!q) return items;
    return items.filter((d) => {
      const hay = `${d.ref_cabinet} ${d.titre ?? ''} ${d.client_name ?? ''}`.toLocaleLowerCase(
        'fr'
      );
      return hay.includes(q);
    });
  }, [items, search]);

  return (
    <div className="mx-auto max-w-6xl space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-serif text-[26px] font-medium leading-tight tracking-[-0.2px] text-text-primary">
            Mes dossiers
          </h1>
          <p className="mt-1 text-[13px] text-text-secondary">
            {loading
              ? '…'
              : `${items.length} dossier${items.length > 1 ? 's' : ''}` +
                (search ? ` · ${filteredItems.length} affiché(s)` : '')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher (ref, titre, client)…"
            className="w-72 rounded-md border border-rule-strong bg-paper-elevated px-3 py-2 text-sm placeholder:text-text-tertiary focus:border-accent focus:outline-none"
          />
          {isAdmin && (
            <Btn
              variant="ghost"
              onClick={() => {
                const next = !showTrash;
                setShowTrash(next);
                if (next) void loadTrash();
              }}
            >
              {showTrash ? 'Fermer la corbeille' : 'Corbeille'}
            </Btn>
          )}
          {!creating && (
            <Btn variant="primary" onClick={() => setCreating(true)}>
              + Nouveau dossier
            </Btn>
          )}
        </div>
      </div>

      {isAdmin && showTrash && (
        <div className="rounded-lg border border-rule bg-paper-card p-4">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-serif text-[16px] font-medium text-text-primary">
              Corbeille
              <span className="ml-2 text-[12px] font-normal text-text-tertiary">
                purge automatique après 30 jours
              </span>
            </h2>
          </div>
          {trashLoading ? (
            <p className="text-[13px] text-text-secondary">Chargement…</p>
          ) : trashItems.length === 0 ? (
            <p className="rounded-md border border-dashed border-rule p-6 text-center text-[13px] text-text-tertiary">
              Corbeille vide.
            </p>
          ) : (
            <ul className="space-y-2">
              {trashItems.map((d) => {
                const days = daysUntil(d.purge_after);
                return (
                  <li
                    key={d.id}
                    className="flex flex-wrap items-center gap-3 rounded-md border border-rule bg-paper-elevated p-3"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[13px] text-text-primary">
                        <span className="font-mono text-[12px] text-text-secondary">
                          {d.ref_cabinet}
                        </span>
                        {d.titre && <span className="ml-2 font-serif">{d.titre}</span>}
                        {d.client_name && (
                          <span className="ml-2 text-[11px] text-text-tertiary">
                            · {d.client_name}
                          </span>
                        )}
                      </p>
                      <p className="mt-0.5 text-[11px] text-text-tertiary">
                        {days !== null && days > 0
                          ? `Purge dans ${days} jour${days > 1 ? 's' : ''}`
                          : 'Purge imminente'}
                      </p>
                    </div>
                    {d.has_archive && (
                      <button
                        type="button"
                        onClick={() => onDownloadArchive(d)}
                        className="rounded-md border border-rule-strong bg-paper-elevated px-2 py-1 text-[11px] hover:bg-paper-subtle"
                      >
                        Télécharger l'archive ZIP
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => onRestore(d)}
                      className="rounded-md border border-accent/40 bg-accent-lighter px-2 py-1 text-[11px] text-accent-text hover:bg-accent/10"
                    >
                      Restaurer
                    </button>
                    <button
                      type="button"
                      onClick={() => onPurge(d)}
                      className="rounded-md border border-cat-def/40 bg-cat-def-pastel px-2 py-1 text-[11px] text-cat-def hover:bg-cat-def/10"
                    >
                      Supprimer définitivement
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {error && (
        <p className="rounded-md border border-cat-def/40 bg-cat-def-pastel px-3 py-2 text-sm text-cat-def">
          {error}
        </p>
      )}

      {creating && (
        <form
          onSubmit={onCreate}
          className="space-y-4 rounded-lg border-[1.5px] border-dashed border-accent bg-accent-lighter p-5"
        >
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <label className="block">
              <span className="block text-[10px] font-medium uppercase tracking-[1px] text-accent-text">
                Référence cabinet *
              </span>
              <input
                autoFocus
                value={refCabinet}
                onChange={(e) => setRefCabinet(e.target.value)}
                placeholder="24/567"
                className="mt-1.5 w-full rounded-md border border-rule-strong bg-paper-elevated px-3 py-2 font-mono text-sm focus:border-accent focus:outline-none"
              />
            </label>
            <label className="block">
              <span className="block text-[10px] font-medium uppercase tracking-[1px] text-accent-text">
                Nom du dossier
              </span>
              <input
                value={titre}
                onChange={(e) => setTitre(e.target.value)}
                placeholder="ex. Affaire Dupont — vol qualifié"
                className="mt-1.5 w-full rounded-md border border-rule-strong bg-paper-elevated px-3 py-2 text-sm focus:border-accent focus:outline-none"
              />
            </label>
            <label className="block md:col-span-2">
              <span className="block text-[10px] font-medium uppercase tracking-[1px] text-accent-text">
                Client
              </span>
              <input
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                placeholder="ex. VERCAUTEREN Steve"
                className="mt-1.5 w-full rounded-md border border-rule-strong bg-paper-elevated px-3 py-2 text-sm focus:border-accent focus:outline-none"
              />
              <span className="mt-1 block text-[11px] text-text-secondary">
                Nom du client du dossier. Permet à la recherche IA de résoudre
                « mon client » vers la bonne personne (rappel des pièces le concernant).
              </span>
            </label>
          </div>

          <div>
            <label className="block">
              <span className="text-[10px] font-medium uppercase tracking-[1px] text-accent-text">
                Date des faits (optionnel)
              </span>
              <input
                type="date"
                value={dateFaits}
                onChange={(e) => setDateFaits(e.target.value)}
                className="mt-1.5 w-full rounded-md border border-rule-strong bg-paper-elevated px-3 py-2 text-sm focus:border-accent focus:outline-none"
              />
              <span className="mt-1 block text-[11px] text-text-secondary">
                Sert à choisir le régime applicable (ancien CC ou Livre 6) quand un poste de dommage
                est en jeu. Pas obligatoire.
              </span>
            </label>
          </div>

          <div>
            <label className="flex items-start gap-2 text-[13px]">
              <input
                type="checkbox"
                checked={mistralOcrAllowed}
                onChange={(e) => setMistralOcrAllowed(e.target.checked)}
                className="mt-0.5"
              />
              <span>
                <span className="font-medium text-text-primary">
                  Autoriser Mistral OCR (cloud)
                </span>
                <span className="mt-0.5 block text-[11px] text-text-secondary">
                  Pour les pièces &gt; 30 pages, l'OCR est envoyé à l'API Mistral
                  (api.mistral.ai, France) — plus rapide et meilleure qualité. Décocher pour
                  forcer le pipeline 100% on-prem (Tesseract local) sur les dossiers
                  ultra-sensibles. Modifiable plus tard depuis l'admin.
                </span>
              </span>
            </label>
          </div>

          <fieldset className="space-y-2">
            <legend className="text-[10px] font-medium uppercase tracking-[1px] text-accent-text">
              Mode de paiement
            </legend>
            <div className="flex flex-wrap gap-3 text-[13px]">
              <label className="flex items-center gap-1.5">
                <input
                  type="radio"
                  name="payment_mode"
                  value=""
                  checked={paymentMode === null}
                  onChange={() => setPaymentMode(null)}
                />
                Non précisé
              </label>
              <label className="flex items-center gap-1.5">
                <input
                  type="radio"
                  name="payment_mode"
                  value="paying"
                  checked={paymentMode === 'paying'}
                  onChange={() => setPaymentMode('paying')}
                />
                Payant
              </label>
              <label className="flex items-center gap-1.5">
                <input
                  type="radio"
                  name="payment_mode"
                  value="baj"
                  checked={paymentMode === 'baj'}
                  onChange={() => setPaymentMode('baj')}
                />
                BAJ (pro deo)
              </label>
            </div>
          </fieldset>

          <fieldset className="space-y-2">
            <legend className="text-[10px] font-medium uppercase tracking-[1px] text-accent-text">
              Qui a accès
            </legend>
            <div className="flex flex-wrap gap-3 text-[13px]">
              <label className="flex items-center gap-1.5">
                <input
                  type="radio"
                  name="access_mode"
                  value="all"
                  checked={accessMode === 'all'}
                  onChange={() => setAccessMode('all')}
                />
                Tout le cabinet
              </label>
              <label className="flex items-center gap-1.5">
                <input
                  type="radio"
                  name="access_mode"
                  value="restricted"
                  checked={accessMode === 'restricted'}
                  onChange={() => setAccessMode('restricted')}
                />
                Sélection
              </label>
              <span className="text-text-tertiary">
                (les administrateurs voient toujours tous les dossiers)
              </span>
            </div>
            {accessMode === 'restricted' && (
              <div className="rounded-md border border-rule bg-paper-elevated p-3">
                {users.length === 0 ? (
                  <p className="text-[12px] text-text-tertiary">Chargement des utilisateurs…</p>
                ) : (
                  <ul className="grid grid-cols-1 gap-1.5 md:grid-cols-2">
                    {users.map((u) => (
                      <li key={u.id}>
                        <label className="flex items-center gap-2 text-[13px]">
                          <input
                            type="checkbox"
                            checked={collaboratorIds.has(u.id)}
                            onChange={() => toggleCollab(u.id)}
                          />
                          <span>
                            {u.display_name}
                            <span className="ml-1.5 font-mono text-[10px] uppercase text-text-tertiary">
                              {u.role}
                            </span>
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="mt-2 text-[11px] text-text-tertiary">
                  Tu es automatiquement ajouté comme propriétaire.
                </p>
              </div>
            )}
          </fieldset>

          {createError && <p className="text-xs text-cat-def">{createError}</p>}
          <div className="flex justify-end gap-2">
            <Btn
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setCreating(false);
                setRefCabinet('');
                setTitre('');
                setAccessMode('all');
                setCollaboratorIds(new Set());
                setCreateError(null);
              }}
            >
              Annuler
            </Btn>
            <Btn
              type="submit"
              variant="primary"
              size="sm"
              disabled={createBusy || !refCabinet.trim()}
            >
              {createBusy ? 'Création…' : 'Créer'}
            </Btn>
          </div>
        </form>
      )}

      {!loading && items.length === 0 && !creating && (
        <p className="rounded-md border border-dashed border-rule bg-paper-card p-10 text-center text-sm text-text-secondary">
          Aucun dossier pour l'instant. Clique sur « + Nouveau dossier » pour démarrer.
        </p>
      )}

      {filteredItems.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-rule bg-paper-elevated">
          <table className="min-w-full text-[13px]">
            <thead className="border-b border-rule bg-paper-subtle text-[11px] uppercase tracking-[0.3px] text-text-tertiary">
              <tr>
                <th className="px-3 py-2 text-left font-medium">Référence</th>
                <th className="px-3 py-2 text-left font-medium">Nom / Client</th>
                <th className="px-3 py-2 text-left font-medium">Pièces</th>
                <th className="px-3 py-2 text-left font-medium">Avancement</th>
                <th className="px-3 py-2 text-right font-medium">Activité</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((d) => {
                const pct =
                  d.pages_total > 0 ? Math.round((d.pages_treated / d.pages_total) * 100) : 0;
                return (
                  <tr
                    key={d.id}
                    className="border-b border-rule last:border-b-0 hover:bg-paper-subtle"
                  >
                    <td className="px-3 py-2.5 font-mono text-[12px] text-text-secondary">
                      <Link
                        to={`/dossiers/${d.id}`}
                        className="text-text-primary no-underline hover:text-text-link"
                      >
                        {formatDossierRef(d.ref_cabinet, d.payment_mode)}
                      </Link>
                    </td>
                    <td className="px-3 py-2.5">
                      <Link
                        to={`/dossiers/${d.id}`}
                        className="text-text-primary no-underline hover:text-text-link"
                      >
                        <span className="font-serif">
                          {d.titre || <em className="text-text-tertiary">sans titre</em>}
                        </span>
                        {d.client_name && d.titre !== d.client_name && (
                          <span className="ml-2 text-[11px] text-text-tertiary">
                            · {d.client_name}
                          </span>
                        )}
                      </Link>
                    </td>
                    <td className="px-3 py-2.5 text-text-secondary">
                      {d.pieces_count > 0 ? (
                        <>
                          {d.pieces_count} pièce{d.pieces_count > 1 ? 's' : ''}
                          {d.annotations_count > 0 && (
                            <span className="ml-1 text-[11px] text-text-tertiary">
                              · {d.annotations_count} annot.
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="text-text-tertiary">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      {d.pages_total > 0 ? (
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 w-20 overflow-hidden rounded-full bg-paper-subtle">
                            <div
                              className={`h-full rounded-full transition-[width] ${
                                pct === 100 ? 'bg-cat-fav' : pct > 0 ? 'bg-accent' : ''
                              }`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <span className="font-mono text-[11px] text-text-tertiary">
                            {d.pages_treated}/{d.pages_total}
                          </span>
                        </div>
                      ) : (
                        <span className="text-text-tertiary">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right text-[11px] text-text-tertiary">
                      {relativeAge(d.last_activity_at)}
                      {d.status !== 'active' && (
                        <span className="ml-2 rounded-pill bg-status-archived-bg px-2 py-0.5 text-[10px] text-status-archived-text">
                          {d.status}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {search && filteredItems.length === 0 && items.length > 0 && (
        <p className="text-[13px] text-text-tertiary">
          Aucun dossier ne correspond à « {search} ».
        </p>
      )}
    </div>
  );
}
